import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { classApi } from '../../../api/classes';
import { useAuth } from '../../../contexts/AuthContext';
import StoreBanner from '../../../components/store-banner';
import './StudentJoinPage.css';

/**
 * StudentJoinPage - Public invite claim page
 * Route: /join?invite=:token
 * No auth required — accessed via invite email link
 * Branded per the inviting organisation's settings.
 */
const StudentJoinPage = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { login: setUserInContext } = useAuth();
    const token = searchParams.get('invite');
    const rootRef = useRef(null);

    const [preview, setPreview] = useState(null);
    const [isLoadingPreview, setIsLoadingPreview] = useState(true);
    const [previewError, setPreviewError] = useState('');

    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [isClaiming, setIsClaiming] = useState(false);
    const [claimed, setClaimed] = useState(false);
    const [logoBase64, setLogoBase64] = useState(null);

    useEffect(() => {
        if (!token) {
            setPreviewError('No invite token provided. Please use the link from your invite email.');
            setIsLoadingPreview(false);
            return;
        }

        const fetchPreview = async () => {
            try {
                const response = await classApi.previewInvite(token);
                if (response.ok) {
                    const data = response.data;
                    setPreview(data);
                    setFirstName(data.firstName || '');
                    setLastName(data.lastName || '');

                    // Apply org branding as CSS custom properties
                    const b = data.branding;
                    if (b && rootRef.current) {
                        if (b.headerColor) rootRef.current.style.setProperty('--sj-header-color', b.headerColor);
                        if (b.headerTextColor) rootRef.current.style.setProperty('--sj-header-text', b.headerTextColor);
                        if (b.backgroundColor) rootRef.current.style.setProperty('--sj-bg-color', b.backgroundColor);
                        if (b.buttonColor) rootRef.current.style.setProperty('--sj-btn-color', b.buttonColor);
                        if (b.buttonTextColor) rootRef.current.style.setProperty('--sj-btn-text', b.buttonTextColor);
                    }

                    // Fetch logo if available
                    if (data.orgLogoKey) {
                        try {
                            const logoRes = await classApi.getPublicOrgLogo(data.orgLogoKey);
                            if (logoRes.ok && logoRes.data?.imageBase64) {
                                const raw = logoRes.data.imageBase64;
                                setLogoBase64(raw.startsWith('data:') ? raw : `data:image/jpeg;base64,${raw}`);
                            }
                        } catch (err) {
                            // Logo fetch failed — page still usable without logo
                        }
                    }
                } else if (response.status === 410) {
                    setPreviewError('This invite link has expired. Please ask your instructor for a new invite.');
                } else if (response.status === 404) {
                    setPreviewError('This invite link is not valid. Please check the link in your email.');
                } else {
                    setPreviewError(response.data?.message || 'Invalid invite link.');
                }
            } catch {
                setPreviewError('Failed to load invite details.');
            } finally {
                setIsLoadingPreview(false);
            }
        };

        fetchPreview();
    }, [token]);

    // The invite email already has a Dance Localé account: the student joins by entering THAT
    // account's password (verified server-side) instead of creating a new account.
    const hasAccount = preview?.hasAccount === true;

    const handleClaim = async (e) => {
        e.preventDefault();

        if (hasAccount) {
            if (!password) {
                toast.error('Please enter your password');
                return;
            }
        } else {
            if (!firstName.trim()) {
                toast.error('First name is required');
                return;
            }
            if (!lastName.trim()) {
                toast.error('Last name is required');
                return;
            }
            if (!password || password.length < 6) {
                toast.error('Password must be at least 6 characters');
                return;
            }
            if (password !== confirmPassword) {
                toast.error('Passwords do not match');
                return;
            }
        }

        setIsClaiming(true);
        try {
            const response = await classApi.claimInvite(
                hasAccount
                    ? { inviteToken: token, password }
                    : { inviteToken: token, firstName: firstName.trim(), lastName: lastName.trim(), password }
            );

            if (response.ok) {
                const { accessToken, refreshToken } = response.data;
                localStorage.setItem('accessToken', accessToken);
                localStorage.setItem('refreshToken', refreshToken);
                const payload = JSON.parse(atob(accessToken.split('.')[1]));
                setUserInContext({
                    accountId: payload.accountId,
                    email: payload.email,
                    organisationId: payload.organisationId,
                    permissions: payload.permissions,
                });
                setClaimed(true);
                toast.success(hasAccount ? 'Welcome back! You have joined.' : 'Account created! Welcome to Dance Localé.');
                setTimeout(() => navigate('/my-hub'), 2000);
            } else if (response.status === 409) {
                toast.error('This invite has already been claimed. Try logging in.');
            } else if (response.status === 410) {
                toast.error('This invite has expired. Please ask your instructor for a new one.');
            } else {
                toast.error(response.data?.message || (hasAccount ? 'Failed to join' : 'Failed to create account'));
            }
        } catch {
            toast.error(hasAccount ? 'Failed to join. Please try again.' : 'Failed to create account. Please try again.');
        } finally {
            setIsClaiming(false);
        }
    };

    if (isLoadingPreview) {
        return (
            <div className="student-join-page" ref={rootRef}>
                <div className="student-join-card">
                    <p className="student-join-loading">Loading invite details...</p>
                </div>
            </div>
        );
    }

    if (previewError) {
        return (
            <div className="student-join-page" ref={rootRef}>
                <div className="student-join-card student-join-error">
                    <h2>Invite Invalid</h2>
                    <p>{previewError}</p>
                </div>
            </div>
        );
    }

    if (claimed) {
        return (
            <div className="student-join-page" ref={rootRef}>
                <div className="student-join-card student-join-success">
                    <h2>{hasAccount ? "You're In!" : 'Account Created!'}</h2>
                    <p>Welcome to Dance Localé. Taking you to your hub...</p>
                    <div className="student-join-app-cta">
                        <p className="student-join-app-cta-text">You can also manage your bookings and check in to classes on the app</p>
                        <StoreBanner />
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="student-join-page" ref={rootRef}>
            <div className="student-join-card">
                <div className="student-join-header">
                    {logoBase64 && (
                        <img
                            src={logoBase64}
                            alt={preview?.orgName || 'School logo'}
                            className="student-join-logo"
                        />
                    )}
                    <div className="student-join-header-text">
                        <h1 className="student-join-title">
                            {preview?.orgName ? `Join ${preview.orgName}` : "You've Been Invited!"}
                        </h1>
                        <p className="student-join-subtitle">
                            {hasAccount
                                ? 'You already have a Dance Localé account. Enter your password to join and see your class schedule.'
                                : 'Create your account to view your class schedule, track your progress, and manage your bookings.'}
                        </p>
                    </div>
                </div>

                <form onSubmit={handleClaim} className="student-join-form">
                    {!hasAccount && (
                        <>
                            <label>First Name *</label>
                            <input
                                type="text"
                                value={firstName}
                                onChange={e => setFirstName(e.target.value)}
                                placeholder="First name"
                                required
                            />

                            <label>Last Name *</label>
                            <input
                                type="text"
                                value={lastName}
                                onChange={e => setLastName(e.target.value)}
                                placeholder="Last name"
                                required
                            />
                        </>
                    )}

                    {preview?.email && (
                        <>
                            <label>Email</label>
                            <input
                                type="email"
                                value={preview.email}
                                readOnly
                                className="student-join-readonly"
                            />
                        </>
                    )}

                    {hasAccount ? (
                        <>
                            <label>Your Dance Localé Password *</label>
                            <input
                                type="password"
                                name="password"
                                autoComplete="current-password"
                                value={password}
                                onChange={e => setPassword(e.target.value)}
                                placeholder="Your password"
                                required
                            />
                            <p className="student-join-hint">
                                <a href="/forgot-password">Forgot your password?</a>
                            </p>
                        </>
                    ) : (
                        <>
                            <label>Password *</label>
                            <p className="student-join-hint">At least 6 characters</p>
                            <input
                                type="password"
                                name="password"
                                autoComplete="new-password"
                                value={password}
                                onChange={e => setPassword(e.target.value)}
                                placeholder="Choose a password"
                                required
                            />

                            <label>Confirm Password *</label>
                            <input
                                type="password"
                                name="confirmPassword"
                                autoComplete="new-password"
                                value={confirmPassword}
                                onChange={e => setConfirmPassword(e.target.value)}
                                placeholder="Repeat password"
                                required
                            />
                        </>
                    )}

                    <button
                        type="submit"
                        className="student-join-btn"
                        disabled={isClaiming}>
                        {hasAccount
                            ? isClaiming ? 'Joining...' : 'Log in and join'
                            : isClaiming ? 'Creating Account...' : 'Create My Account'}
                    </button>
                </form>

                {!hasAccount && (
                    <p className="student-join-login-link">
                        Already have an account? <a href="/login">Log in</a>
                    </p>
                )}
                <p className="student-join-powered-by">Powered by Dance Localé</p>
            </div>
        </div>
    );
};

export default StudentJoinPage;
